import { useEffect, useState } from 'react'
import { ShieldAlert, X } from 'lucide-react'
import { Spinner } from '@/components/ui'
import { useApprovalToastStore } from '@/stores/approvalToastStore'
import { useApproveApproval, useRejectApproval } from '@/hooks/useApprovals'

export function ApprovalToast() {
  const { pending, dismiss } = useApprovalToastStore()
  const approve = useApproveApproval()
  const reject = useRejectApproval()
  const [activeAction, setActiveAction] = useState<'approve' | 'reject' | null>(null)

  useEffect(() => {
    if (!pending) return
    const timer = setTimeout(() => dismiss(), 30000)
    return () => clearTimeout(timer)
  }, [pending, dismiss])

  if (!pending) return null

  const handleApprove = async () => {
    setActiveAction('approve')
    try {
      await approve.mutateAsync(pending.approval_id)
      dismiss()
    } finally {
      setActiveAction(null)
    }
  }

  const handleReject = async () => {
    setActiveAction('reject')
    try {
      await reject.mutateAsync(pending.approval_id)
      dismiss()
    } finally {
      setActiveAction(null)
    }
  }

  const isBusy = activeAction !== null

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-card border border-border rounded-lg p-4 shadow-lg max-w-sm">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5">
          <ShieldAlert className="w-4 h-4 text-warning shrink-0" />
          <span className="text-sm font-medium text-foreground">
            Tool call needs approval
          </span>
        </div>
        <button
          onClick={dismiss}
          className="text-muted-foreground hover:text-foreground shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs font-mono text-foreground mb-3">
        {pending.capability_id}
      </p>
      <div className="flex gap-2">
        <button
          onClick={handleApprove}
          disabled={isBusy}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-success hover:bg-success/90 text-success-foreground disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {activeAction === 'approve' && <Spinner className="w-3 h-3" />}
          Approve
        </button>
        <button
          onClick={handleReject}
          disabled={isBusy}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-destructive hover:bg-destructive/90 text-destructive-foreground disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {activeAction === 'reject' && <Spinner className="w-3 h-3" />}
          Reject
        </button>
      </div>
    </div>
  )
}
